import classNames from "classnames"
import {
  Ref,
  cloneElement,
  forwardRef,
  MouseEvent,
  ReactElement,
  ElementType,
  useCallback,
  ReactNode,
} from "react"
import { Link as ReactRouterLink } from "react-router-dom"
import {
  BASE_CLASSES,
  BASE_CLASSES_DISABLED,
  BASE_CLASSES_MD,
  BASE_CLASSES_SM,
  COLOUR_SCHEME,
  COLOUR_SCHEME_DISABLED,
  COLOUR_SCHEME_FOCUS,
  COLOUR_SCHEME_HOVER,
} from "./constants"
import type { LinkBaseProps, LinkProps } from "./types"
import { twMerge } from "tailwind-merge"

export const Link: <E extends ElementType = typeof ReactRouterLink>(
  props: LinkProps<E>,
) => ReactNode | null = forwardRef(
  (
    {
      as = ReactRouterLink,
      children,
      className,
      disabled,
      variation = "primary",
      size = "md",
      startAdornment,
      endAdornment,
      onClick,
      ...rest
    }: LinkBaseProps,
    ref: Ref<HTMLAnchorElement>,
  ) => {
    const isMD = size === "md"
    const isSM = size === "sm"

    const Component = as

    const _className = twMerge(
      classNames(
        BASE_CLASSES,
        COLOUR_SCHEME[variation],
        {
          [BASE_CLASSES_MD]: isMD,
          [BASE_CLASSES_SM]: isSM,
          [BASE_CLASSES_DISABLED]: disabled,
          [COLOUR_SCHEME_DISABLED[variation]]: disabled,
          [COLOUR_SCHEME_FOCUS[variation]]: !disabled,
          [COLOUR_SCHEME_HOVER[variation]]: !disabled,
        },
        className,
      ),
    )

    const handleClick = useCallback(
      (e: MouseEvent<HTMLAnchorElement>) => {
        if (disabled) {
          e.preventDefault()
          return
        }

        onClick?.(e)
      },
      [disabled, onClick],
    )

    const getCloneElementWithSizeProp = (element: ReactElement) =>
      cloneElement(element, {
        size: isSM ? "xs" : "sm",
      })

    return (
      <Component
        aria-disabled={disabled}
        className={_className}
        ref={ref}
        onClick={handleClick}
        {...rest}
      >
        {startAdornment && getCloneElementWithSizeProp(startAdornment)}
        {children}
        {endAdornment && getCloneElementWithSizeProp(endAdornment)}
      </Component>
    )
  },
)
